import React from 'react';
import { View, ScrollView } from 'react-native';

import Item from './components/item';
import OperationInfoTile from './components/OperationInfoTile';
import Loading, { SmallLoading } from '@components/loading';
import Refresh from '@components/common/Refresh';

import { calculateTableConfig, deepClone } from '@helpers';

import { scale as initialScale, fullWidth } from '@components/frame';
import {
  TILE_CONFIG,
  SCREEN_PADDING_HORIZONTAL,
  SCREEN_PADDING_VERTICAL_FOR_ITEM,
  TABLE_NAMES,
  TEST_IDS,
} from '../../v2/pages/common/constants/base.constants';
import { font as initialFont } from '@styles/fonts';
import DataGrid from '@components/common/DataGrid';
import { updateImageMetadata } from '@reducers/imageHandling';
import { connect } from 'react-redux';

import { getStatusConfig, getDataGridColumns } from './helpers';
import { useManageOperations } from './hook/useManageOperations';

const Operations = (props) => {
  const {
    totalSearchResult,
    searchResult,
    isError,
    loaded,
    C,
    isSmallMode,
    isLineMode,
    isInfoTilesMode,
    fetchData,
    generateImageURL,
    recentlyViewed,
    setRecentlyViewed,
    isTableMode,
    width,
    withoutHorizontalPaddings,
    withoutHorizontalPaddingsForPopupMode,
    style,
    attachMode,
    attachImageId,
    updateImageMetadata,
    onItemSelect,
    isPopupMode,
    popupWidth,
    containerWidth = fullWidth,
    scale = initialScale,
    font = initialFont,
    customBackgroundColor,
    statContextStorage,
    jobMainAppTileView,
    shouldUseDefaultHorizontalPaddings,
    preferredMachineShouldBeClickable,
  } = props;

  const effectiveWidth = isPopupMode && popupWidth ? popupWidth : containerWidth;

  const { open } = useManageOperations({
    isPopupMode,
    attachMode,
    attachImageId,
    onItemSelect,
    updateImageMetadata,
  });

  const getStatusConfigFn = (status) => getStatusConfig(status, C, scale);

  const dataGridColumns = getDataGridColumns({ scale, font, C, open, attachMode });

  const config = {
    width: width ?? effectiveWidth,
    scale,
    initialBaseSize: isSmallMode ? TILE_CONFIG.BASE_WIDTH_SMALL : TILE_CONFIG.BASE_WIDTH,
    spaceBetween: isSmallMode ? TILE_CONFIG.SPACE_BETWEEN_SMALL : TILE_CONFIG.SPACE_BETWEEN_NORMAL,
  };
  const { columns, itemSize } = calculateTableConfig(config);

  const data = deepClone(searchResult);

  const additions = columns - (data.length % columns);
  if (additions !== columns) {
    for (let i = 0; i < additions; i++) {
      data.push('empty');
    }
  }

  if (isTableMode) {
    return (
      <View testID={TEST_IDS.searchScreen.operations.dataGridContainer} style={{ width: width ?? effectiveWidth, height: '100%' }}>
        <DataGrid
          {...{
            ...props,
            bg: customBackgroundColor,
            onRowClick: open,
            columns: dataGridColumns,
            data,
            primaryKey: 'productionOperationNumber',
            idName: 'productionOperationId',
            tableName: props.tableName || TABLE_NAMES.productionOperation,
          }}
        />
      </View>
    );
  }

  if (isError) {
    return <Refresh onPress={fetchData} />;
  }

  // Info Tiles Mode - similar to Batches
  if (isInfoTilesMode) {
    return (
      <View
        testID={TEST_IDS.searchScreen.operations.infoTileContainer}
        style={{
          width: width ?? effectiveWidth,
          flex: 1,
        }}
      >
        {loaded ? (
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              paddingBottom: scale(20),
              paddingTop: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM),
              paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
            }}
          >
            {searchResult.map((item, index) => (
              <OperationInfoTile
                key={`operation-tile-${index}-${item.productionOperationId}`}
                item={item}
                C={C}
                font={font}
                scale={scale}
                onPress={open}
                width={width ?? effectiveWidth - scale(withoutHorizontalPaddings ? 0 : SCREEN_PADDING_HORIZONTAL * 2)}
                getStatusConfig={getStatusConfigFn}
                statContextStorage={statContextStorage}
                jobMainAppTileView={jobMainAppTileView}
                preferredMachineShouldBeClickable={preferredMachineShouldBeClickable}
              />
            ))}
            {totalSearchResult !== searchResult?.length && (
              <View
                style={{
                  marginTop: scale(20),
                  justifyContent: 'center',
                  backgroundColor: customBackgroundColor || C('Background / Light / System'),
                }}
              >
                <SmallLoading />
              </View>
            )}
          </ScrollView>
        ) : (
          <Loading background={customBackgroundColor} />
        )}
      </View>
    );
  }

  return (
    <View testID={TEST_IDS.searchScreen.operations.itemContainer} style={{ width: width ?? effectiveWidth, height: '100%' }}>
      {loaded ? (
        <View>
          <View
            style={[
              {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingLeft: scale(
                  shouldUseDefaultHorizontalPaddings
                    ? SCREEN_PADDING_HORIZONTAL
                    : withoutHorizontalPaddings
                    ? 0
                    : isPopupMode
                    ? 20
                    : SCREEN_PADDING_HORIZONTAL
                ),
                paddingRight: scale(
                  shouldUseDefaultHorizontalPaddings
                    ? SCREEN_PADDING_HORIZONTAL
                    : withoutHorizontalPaddings
                    ? 0
                    : isPopupMode
                    ? 20
                    : SCREEN_PADDING_HORIZONTAL
                ),
                justifyContent: 'space-between',
                flexDirection: 'row',
                flexWrap: 'wrap',
              },
              style && style,
              isLineMode && { marginTop: 0 },
            ]}
          >
            {data.map((item, index) => (
              <Item
                key={'Item' + index}
                item={item}
                {...{
                  index,
                  itemSize,
                  generateImageURL,
                  isSmallMode,
                  isLineMode,
                  recentlyViewed,
                  setRecentlyViewed,
                  open,
                  attachMode,
                  attachImageId,
                  effectiveWidth,
                  isPopupMode,
                  scale,
                  font,
                  statContextStorage,
                  shouldUseDefaultHorizontalPaddings,
                }}
                isEmpty={item === 'empty'}
              />
            ))}
          </View>
          {totalSearchResult !== searchResult?.length && (
            <View
              style={{
                marginTop: scale(20),
                backgroundColor: customBackgroundColor || C('Background / Light / System'),
                justifyContent: 'center',
              }}
            >
              <SmallLoading />
            </View>
          )}
        </View>
      ) : (
        <Loading background={customBackgroundColor} />
      )}
    </View>
  );
};

export default connect(
  ({ search: { statContextStorage } }) => {
    statContextStorage;
  },
  { updateImageMetadata }
)(Operations);
